import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PhoneIcon from "@mui/icons-material/Phone";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import {
  Button,
  CardActionArea,
  CardMedia,
  Typography,
  TextField,
  Box,
  InputLabel,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  SelectChangeEvent,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DrivingWaterSolutionsWhite from "../../images/DrivingWaterSolutionsWhite.png";
import { HCaptcha } from "@hcaptcha/react-hcaptcha";
import { getTrackingData, storeUTMParams } from "../../tracking/tracking";

// ── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  mobile: string;
  email: string;
  customerCategory: string;
  solutionInterested: string;
  projectLocation: string;
  yourLocation: string;
  message: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

interface LabeledFieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

// ── Dropdown options ──────────────────────────────────────────────────────────

const CUSTOMER_CATEGORIES = [
  "Individual",
  "Business",
  "Gated Community",
  "Institution / Campus",
  "IT Park",
  "Industry",
  "Others",
];

const SOLUTIONS = [
  "Stormwater Management",
  "Rainwater Harvesting",
  "Sponge Park",
  "Eco Bloc",
  "Not Sure / Need Consultation",
];

// ── Shared styles ─────────────────────────────────────────────────────────────
/* 
const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "30px",
    backgroundColor: "#FFFFFF",
    padding: "24px 20px",
    fontSize: "14px",
    "& fieldset": { borderColor: "#D0D5DD" },
    "&:hover fieldset": { borderColor: "#0065FF" },
    "&.Mui-focused fieldset": { borderColor: "#0065FF" },
    "&.Mui-error fieldset": { borderColor: "#d32f2f" },
  },
  "& .MuiInputBase-input": {
    padding: "16px 0px",
    fontSize: "14px",
    color: "#344054",
    "&::placeholder": { color: "#98A2B3", opacity: 1 },
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
    fontSize: "12px",
  },
}; */

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "30px",
    backgroundColor: "#FFFFFF",
    padding: "24px 20px",
    fontSize: "14px",

    "& fieldset": { borderColor: "#D0D5DD" },
    "&:hover fieldset": { borderColor: "#0065FF" },
    "&.Mui-focused fieldset": { borderColor: "#0065FF" },
    "&.Mui-error fieldset": { borderColor: "#d32f2f" },

    // 👇 ADD THIS BLOCK
    "& input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px #FFFFFF inset",
      WebkitTextFillColor: "#344054",
      borderRadius: "30px",
    },
    "& input:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 1000px #FFFFFF inset",
    },
    "& input:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px #FFFFFF inset",
    },
  },

  "& .MuiInputBase-input": {
    padding: "12px 0px",
    fontSize: "14px",
    color: "#344054",

    "&::placeholder": { color: "#98A2B3", opacity: 1 },

    // 👇 OPTIONAL (extra safety)
    "&:-webkit-autofill": {
      WebkitTextFillColor: "#344054",
    },
  },

  "& .MuiFormHelperText-root": {
    marginLeft: 0,
    fontSize: "12px",
  },
};

// Select — matches inputStyles exactly (same padding, pill radius, placeholder color)
const selectStyles = {
  borderRadius: "30px",
  backgroundColor: "#FFFFFF",
  fontSize: "14px",
  color: "#344054",
  padding: "24px 20px !important",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D0D5DD" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#0065FF" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0065FF" },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#d32f2f" },
  // Matches MuiInputBase-input padding in inputStyles: top/bottom 16px, left/right 20px
  "& .MuiSelect-select": {
    padding: "16px 0px !important",
    paddingRight: "40px !important",
    fontSize: "14px",
    color: "#344054",
  },
  // Arrow icon positioning
  "& .MuiSelect-icon": {
    color: "#344054",
    right: "16px",
  },
};

// Textarea-specific: fixes placeholder floating to top-center issue
const textareaStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    fontSize: "14px",
    alignItems: "flex-start",
    padding: "11px 14px",    
    // Force the root wrapper to a fixed height so rows={6} is respected
    minHeight: "160px",
    "& fieldset": { borderColor: "#D0D5DD" },
    "&:hover fieldset": { borderColor: "#0065FF" },
    "&.Mui-focused fieldset": { borderColor: "#0065FF" },
    "&.Mui-error fieldset": { borderColor: "#d32f2f" },
  },
  "& .MuiInputBase-inputMultiline": {
    padding: 0,
    height: "120px !important",
    fontSize: "14px",
    color: "#344054",
    resize: "none" as const,
    // Stretch the textarea to fill the wrapper height
    minHeight: "136px !important",
    "&::placeholder": { color: "#98A2B3", opacity: 1 },
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
    fontSize: "12px",
  },
};

// ── Sub-component ─────────────────────────────────────────────────────────────

const LabeledField: React.FC<LabeledFieldProps> = ({
  label,
  required = false,
  children,
}) => (
  <Box>
    <InputLabel
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        color: "#344054",
        mb: 0.5,
        whiteSpace: "normal",
        "& span": { color: "#d32f2f" },
      }}
    >
      {label} {required && <span>*</span>}
    </InputLabel>
    {children}
  </Box>
);

// ── Main Component ────────────────────────────────────────────────────────────



const HomeContactInfo: React.FC = () => {
  const navigate = useNavigate();
 const isReactSnap =
    typeof navigator !== "undefined" &&
    navigator.userAgent.includes("ReactSnap");
  const initialForm: FormState = {
    name: "",
    mobile: "",
    email: "",
    customerCategory: "",
    solutionInterested: "",
    projectLocation: "",
    yourLocation: "",
    message: "",
  };

  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const captchaRef = useRef<HCaptcha | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const validate = (): FormErrors => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\+?[0-9]{9,15}$/.test(form.mobile.replace(/\s/g, ""))) {
      newErrors.mobile = "Enter a valid mobile number";
    }
    if (!form.email.trim()) {
      newErrors.email = "Email ID is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!form.customerCategory)
      newErrors.customerCategory = "Please select a customer category";
    if (!form.solutionInterested)
      newErrors.solutionInterested = "Please select a solution";
    if (!form.projectLocation.trim())
      newErrors.projectLocation = "Project location is required";
    return newErrors;
  };

  const handleTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormState]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSelectChange =
    (field: keyof FormState) => (e: SelectChangeEvent<string>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleSubmit = async (): Promise<void> => {

    const trackingData = getTrackingData();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!captchaToken) {
      alert("Please complete captcha");
      return;
    }

    try {

      setLoading(true);

      const payload = {
        name: form.name,
        phone: form.mobile,
        email: form.email,
        message: form.message,
        customerCategory: form.customerCategory,
        solutionInterested: form.solutionInterested,
        projectLocation: form.projectLocation,
        userLocation: form.yourLocation,
        hcaptchaToken: captchaToken,
        tracking: trackingData,
      };


      /*       const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/contact-submit`, { */
      /*    const response = await fetch(`http://localhost:1337/api/contact-submit`, {  */
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/contact-submit`, { 
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData: any = await response.json();
        throw new Error(errorData?.error?.message || "Failed to submit form");
      }


      const result = await response.json();

      if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("event", "generate_lead", {
          event_category: "engagement",
          event_label: "contact_form_submit",
          value: 1,
        });
      }
      /*   console.log("Form submitted successfully:", result); */

      setSnackbarOpen(true);
      setForm(initialForm);  // reset form
      setErrors({});         // clear errors
      captchaRef.current?.resetCaptcha();
      setCaptchaToken(null);

    } catch (error: any) {
      console.error("Form submission error:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const siteKey = process.env.REACT_APP_HCAPTCHA_SITE_KEY!;



  return (
    <Grid
      id="contact-section"
      container
      data-testid="contact-info"
      sx={{
        flexGrow: 1,
        background: "#E3E5E4",
        px: { xs: 3, sm: 5, md: 8, lg: 10 },
        py: { xs: 5, sm: 6, md: 7 },
        mt: { xs: 0, md: "75px" },
        gap: { xs: 5, md: 0 },
      }}
    >
      {/* ── LEFT: Form — wider column (7/12) ── */}
      <Grid size={{ xs: 12, md: 5 }}>
        <Box sx={{ pr: { md: 6, lg: 8 } }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#555",
              mb: 0.5,
              letterSpacing: "0.02em",
            }}
          >
            Contact Us
          </Typography>

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "26px", sm: "30px", md: "36px" },
              color: "#0A0A0A",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            Join us to
            <br />
            Shape a Water secure future
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              color: "#667085",
              mb: 3,
              lineHeight: 1.7,
            }}
          >
            We&apos;d love to hear from you. Fill out the form and our team
            will get back to you within 24&ndash;48 hours.
          </Typography>

          {/* Row 1: Name + Mobile Number */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LabeledField label="Name" required>
                <TextField
                  fullWidth
                  name="name"
                  placeholder="Please enter name"
                  value={form.name}
                  onChange={handleTextChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={inputStyles}
                />
              </LabeledField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LabeledField label="Mobile Number" required>
                <TextField
                  fullWidth
                  name="mobile"
                  placeholder="Please enter mobile number"
                  value={form.mobile}
                  onChange={handleTextChange}
                  error={!!errors.mobile}
                  helperText={errors.mobile}
                  sx={inputStyles}
                />
              </LabeledField>
            </Grid>
          </Grid>

          {/* Row 2: Email ID + Customer Category (Dropdown) */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LabeledField label="Email ID" required>
                <TextField
                  fullWidth
                  name="email"
                  placeholder="Please enter email"
                  value={form.email}
                  onChange={handleTextChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={inputStyles}
                />
              </LabeledField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LabeledField label="Customer Category" required>
                <FormControl fullWidth error={!!errors.customerCategory}>
                  <Select
                    value={form.customerCategory}
                    onChange={handleSelectChange("customerCategory")}
                    displayEmpty
                    sx={selectStyles}
                    renderValue={(val) =>
                      val ? (
                        <span style={{ color: "#344054", fontSize: "14px" }}>{val}</span>
                      ) : (
                        <span style={{ color: "#98A2B3", fontSize: "14px" }}>Select category</span>
                      )
                    }
                  >
                    {CUSTOMER_CATEGORIES.map((opt) => (
                      <MenuItem key={opt} value={opt} sx={{ fontSize: "14px" }}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.customerCategory && (
                    <FormHelperText sx={{ ml: 0, fontSize: "12px" }}>
                      {errors.customerCategory}
                    </FormHelperText>
                  )}
                </FormControl>
              </LabeledField>
            </Grid>
          </Grid>

          {/* Row 3: Solution Interested In (Dropdown) + Project Location */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LabeledField label="Solution Interested In" required>
                <FormControl fullWidth error={!!errors.solutionInterested}>
                  <Select
                    value={form.solutionInterested}
                    onChange={handleSelectChange("solutionInterested")}
                    displayEmpty
                    sx={selectStyles}
                    renderValue={(val) =>
                      val ? (
                        <span style={{ color: "#344054", fontSize: "14px" }}>{val}</span>
                      ) : (
                        <span style={{ color: "#98A2B3", fontSize: "14px" }}>Select solution</span>
                      )
                    }
                  >
                    {SOLUTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt} sx={{ fontSize: "14px" }}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.solutionInterested && (
                    <FormHelperText sx={{ ml: 0, fontSize: "12px" }}>
                      {errors.solutionInterested}
                    </FormHelperText>
                  )}
                </FormControl>
              </LabeledField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LabeledField label="Project Location (Site Location)" required>
                <TextField
                  fullWidth
                  name="projectLocation"
                  placeholder="Where the solution is required"
                  value={form.projectLocation}
                  onChange={handleTextChange}
                  error={!!errors.projectLocation}
                  helperText={errors.projectLocation}
                  InputProps={{
                    startAdornment: (
                      <LocationOnIcon
                        sx={{ color: "#98A2B3", fontSize: 18, mr: 0.5 }}
                      />
                    ),
                  }}
                  sx={inputStyles}
                />
              </LabeledField>
            </Grid>
          </Grid>

          {/* Row 4: Your Location (Optional) */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LabeledField label="Your Location (Optional)">
                <TextField
                  fullWidth
                  name="yourLocation"
                  placeholder="Your current location"
                  value={form.yourLocation}
                  onChange={handleTextChange}
                  sx={inputStyles}
                />
              </LabeledField>
            </Grid>
          </Grid>

          {/* Row 5: Brief Requirement / Message */}
          <Box sx={{ mb: 3 }}>
            <LabeledField label="Brief Requirement / Message (Optional)" required={false}>
              <TextField
                fullWidth
               
                name="message"
                placeholder="Share your inquiry, project details, or how we can collaborate."
                value={form.message}
                onChange={handleTextChange}
                helperText={errors.message}
                multiline
                rows={6}
                sx={textareaStyles}
              />
            </LabeledField>
          </Box>
{!isReactSnap && (
          <HCaptcha
            sitekey={siteKey}
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken(null)}
            ref={captchaRef}
          />)}

          {/* Submit — exact original */}
          <Button

            onClick={handleSubmit}
            disabled={loading}
            sx={{
              mt: 3,
              background: "linear-gradient(90deg, #64e7a5 -31.21%, #0065ff 138.18%)",
              color: "#fff",
              borderRadius: "50px",
              padding: "24px 32px",
              fontSize: "0.9rem",
              fontWeight: 600,
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              "&:hover": { background: "linear-gradient(90deg, #4fd490 -31.21%, #0052d4 138.18%)" },
              boxShadow: "0 2px 8px rgba(0,101,255,0.3)",
              opacity: loading ? 0.6 : 1, // optional: visually show disabled
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Submitting..." : "Submit"}
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ArrowDownwardIcon
                sx={{
                  marginLeft: "15px",
                  transform: "rotate(-40deg)",
                  fontSize: "20px",
                }}
              />
            </Box>
          </Button>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 2 }}></Grid>

      {/* ── RIGHT: Direct Contact + Image ── */}
      <Grid
        size={{ xs: 12, md: 5 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          pl: { md: 4 },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "18px", md: "20px", lg: "22px" },
            fontWeight: 500,
            color: "#0A0A0A",
            mb: 3,
          }}
        >
          Prefer a Direct Approach?
        </Typography>

        {/* Phone */}
        <Box
          onClick={() => (window.location.href = "tel:+918220002200")}
          sx={{
            mb: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            cursor: "pointer",
            "&:hover .contact-label": { textDecoration: "underline" },
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: "linear-gradient(90deg, #64e7a5 -31.21%, #0065ff 138.18%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PhoneIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Typography
            className="contact-label"
            sx={{
              color: "#0065FF",
              fontWeight: 700,
              fontSize: { xs: "16px", md: "17px", lg: "18px" },
            }}
          >
            +91 82200 02200
          </Typography>
        </Box>

        {/* Email */}
        <Box
          onClick={() =>
            (window.location.href = "mailto:contact@stonehands.in")
          }
          sx={{
            mb: 4,
            display: "flex",
            alignItems: "center",
            gap: 2,
            cursor: "pointer",
            "&:hover .contact-label": { textDecoration: "underline" },
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: "linear-gradient(90deg, #64e7a5 -31.21%, #0065ff 138.18%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlternateEmailIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Typography
            className="contact-label"
            sx={{
              color: "#0065FF",
              fontWeight: 700,
              fontSize: { xs: "16px", md: "17px", lg: "18px" },
            }}
          >
            Contact@stonehands.in
          </Typography>
        </Box>

        {/* Circular Image — unchanged */}
        <Box
          sx={{
            position: "relative",
            width: { xs: 220, sm: 270, md: 300, lg: 340 },
            height: { xs: 220, sm: 270, md: 300, lg: 340 },
            borderRadius: "50%",
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(0,101,255,0.18)",
          }}
        >
          <CardMedia
            component="img"
            alt="Water Solutions"
            image={DrivingWaterSolutionsWhite}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <CardActionArea
            onClick={() => {
              window.scrollTo(0, 0);
              navigate("/about");
            }}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
              "& .MuiCardActionArea-focusHighlight": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#0065FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 14px rgba(0,101,255,0.45)",
              }}
            >
              <ArrowForwardIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
          </CardActionArea>
        </Box>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          Your message has been sent successfully!
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default HomeContactInfo;